import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiEdit2, FiMessageSquare, FiRefreshCw, FiSend, FiTool } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { Pagination as PaginationUtils } from "../../utils/pagination.utils";
import { useAuth } from "../../provider/auth.provider";
import {
  addTicketMessage,
  addressTicket,
  getAllTickets,
  getTicketById,
  getTicketMessages,
  updateTicketDetails,
  updateTicketStatus,
} from "../../libs/tickets";
import {
  TicketItem,
  TicketMessage,
  TicketPagination,
  TicketStatus,
} from "../../types/ticket";

import { useUrlFilters } from "../../hooks/useUrlFilters";

const defaultPagination: TicketPagination = {
  offset: 0,
  limit: 10,
  page: 1,
  total_pages: 1,
  total_count: 0,
  has_next_page: false,
  has_prev_page: false,
  next_page: null,
  prev_page: null,
};

const TicketActionsPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canUpdateDetails = hasPermission("ticket", "update");
  const canAddressTicket = hasPermission("ticket", "address");
  const canUpdateTicketStatus = hasPermission("ticket", "update_status");
  const canAddMessage = hasPermission("ticket", "add_message");

  const [tickets, setTickets] = useState<TicketItem[]>([]);
  const [pagination, setPagination] = useState<TicketPagination>(defaultPagination);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<TicketItem | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");

  const [filters, setFilters] = useState(useUrlFilters({
    search: "",
    status: "",
    priority: "",
  }));

  const [editForm, setEditForm] = useState({
    subject: "",
    description: "",
    priority: "medium",
  });

  const [addressForm, setAddressForm] = useState({
    status: "in_progress" as TicketStatus,
    resolution_note: "",
  });

  const [statusForm, setStatusForm] = useState({
    status: "resolved" as TicketStatus,
    resolution_note: "",
  });

  const isFirstFiltersEffect = useRef(true);

  const fetchTickets = async (page = pagination.page, limit = pagination.limit) => {
    setTicketsLoading(true);
    try {
      const response = await getAllTickets({
        page,
        limit,
        search: filters.search || undefined,
        status: filters.status || undefined,
        priority: filters.priority || undefined,
      });

      setTickets(response.tickets);
      setPagination(response.pagination || defaultPagination);

      if (response.tickets.length === 0) {
        setSelectedTicketId(null);
        setSelectedTicket(null);
        setMessages([]);
        return;
      }

      if (!selectedTicketId || !response.tickets.some((ticket) => ticket.id === selectedTicketId)) {
        setSelectedTicketId(response.tickets[0].id);
      }
    } catch (error: any) {
      console.error("Failed to fetch all tickets:", error);
      toast.error(error?.response?.data?.message || "Failed to load tickets");
    } finally {
      setTicketsLoading(false);
    }
  };

  const fetchTicketDetail = async (ticketId: number) => {
    setDetailLoading(true);
    try {
      const detail = await getTicketById(ticketId, { page: 1, limit: 25 });
      setSelectedTicket(detail.ticket);
      if (detail.messages.length > 0) {
        setMessages(detail.messages);
      } else {
        const messageResponse = await getTicketMessages(ticketId, { page: 1, limit: 50 });
        setMessages(messageResponse.messages);
      }
    } catch (error: any) {
      console.error("Failed to fetch ticket detail:", error);
      toast.error(error?.response?.data?.message || "Failed to load ticket details");
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-apply All Tickets search & filters with debouncing
  useEffect(() => {
    if (isFirstFiltersEffect.current) {
      isFirstFiltersEffect.current = false;
      return;
    }

    const handler = setTimeout(() => {
      fetchTickets(1, pagination.limit);
    }, 400);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.limit]);

  useEffect(() => {
    if (selectedTicketId) {
      fetchTicketDetail(selectedTicketId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicketId]);

  useEffect(() => {
    if (!selectedTicket) return;
    setEditForm({
      subject: selectedTicket.subject || "",
      description: selectedTicket.description || "",
      priority: selectedTicket.priority || "medium",
    });
    setAddressForm((prev) => ({ ...prev, status: (selectedTicket.status as TicketStatus) || "in_progress" }));
    setStatusForm((prev) => ({ ...prev, status: (selectedTicket.status as TicketStatus) || "resolved" }));
  }, [selectedTicket]);

  const handleUpdateDetails = async () => {
    if (!selectedTicketId) return;
    if (!editForm.subject.trim() || !editForm.description.trim()) {
      toast.error("Subject and description are required");
      return;
    }

    setActionLoading(true);
    try {
      await updateTicketDetails(selectedTicketId, {
        subject: editForm.subject.trim(),
        description: editForm.description.trim(),
        priority: editForm.priority,
      });
      await fetchTicketDetail(selectedTicketId);
      await fetchTickets(pagination.page, pagination.limit);
    } catch (error) {
      console.error("Failed to update ticket details:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddressTicket = async () => {
    if (!selectedTicketId) return;
    setActionLoading(true);
    try {
      await addressTicket(selectedTicketId, {
        status: addressForm.status,
        resolution_note: addressForm.resolution_note.trim() || undefined,
      });
      await fetchTicketDetail(selectedTicketId);
      await fetchTickets(pagination.page, pagination.limit);
    } catch (error) {
      console.error("Failed to address ticket:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedTicketId) return;
    setActionLoading(true);
    try {
      await updateTicketStatus(selectedTicketId, {
        status: statusForm.status,
        resolution_note: statusForm.resolution_note.trim() || undefined,
      });
      await fetchTicketDetail(selectedTicketId);
      await fetchTickets(pagination.page, pagination.limit);
    } catch (error) {
      console.error("Failed to update ticket status:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddMessage = async () => {
    if (!selectedTicketId) return;
    if (!newMessage.trim()) {
      toast.error("Message cannot be empty");
      return;
    }

    setActionLoading(true);
    try {
      await addTicketMessage(selectedTicketId, newMessage.trim());
      setNewMessage("");
      await fetchTicketDetail(selectedTicketId);
    } catch (error) {
      console.error("Failed to add ticket message:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const orderedMessages = useMemo(
    () =>
      [...messages].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      ),
    [messages]
  );

  const statusBadge = (status?: string) => {
    const value = (status || "").toLowerCase();
    if (value === "resolved") return "bg-green-100 text-green-700";
    if (value === "in_progress") return "bg-blue-100 text-blue-700";
    if (value === "closed") return "bg-gray-100 text-gray-700";
    return "bg-yellow-100 text-yellow-700";
  };

  const priorityBadge = (priority?: string) => {
    const value = (priority || "").toLowerCase();
    if (value === "critical") return "bg-red-100 text-red-700";
    if (value === "high") return "bg-orange-100 text-orange-700";
    if (value === "medium") return "bg-blue-100 text-blue-700";
    return "bg-gray-100 text-gray-700";
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Ticket Actions</h1>
            <p className="text-sm text-gray-600 mt-1">
              View all tickets and perform addressing, status updates, and responses.
            </p>
          </div>
          <button
            onClick={() => fetchTickets(pagination.page, pagination.limit)}
            className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <div className="xl:col-span-2 bg-white rounded shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">All Tickets</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
              <input
                type="text"
                placeholder="Search"
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded text-sm"
              />
              <select
                value={filters.status}
                onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded text-sm"
              >
                <option value="">All status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <select
                value={filters.priority}
                onChange={(e) => setFilters((prev) => ({ ...prev, priority: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded text-sm"
              >
                <option value="">All priority</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            {/*
            <button
              onClick={() => fetchTickets(1, pagination.limit)}
              className="mt-3 px-3 py-2 text-sm bg-gray-100 rounded hover:bg-gray-200"
            >
              Apply Filters
            </button>
            */}
          </div>

          <div className="divide-y divide-gray-200 max-h-[480px] overflow-y-auto">
            {ticketsLoading ? (
              <div className="p-6 text-sm text-gray-500">Loading tickets...</div>
            ) : tickets.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">No tickets found.</div>
            ) : (
              tickets.map((ticket) => (
                <button
                  key={ticket.id}
                  onClick={() => setSelectedTicketId(ticket.id)}
                  className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${selectedTicketId === ticket.id ? "bg-blue-50 border-l-4 border-l-blue-600" : ""
                    }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    {/* <p className="font-medium text-gray-900 text-sm">{ticket.subject}</p> */}
                    {/* <p className="font-medium text-gray-900 text-sm">
                      <span className="text-xs text-gray-500 mr-1">#{ticket.id}</span>
                      {ticket.subject}
                    </p> */}
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500">#{ticket.id}</span>
                      <p className="font-medium text-gray-900 text-sm">{ticket.subject}</p>
                    </div>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded ${priorityBadge(
                        ticket.priority
                      )}`}
                    >
                      {ticket.priority}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded ${statusBadge(ticket.status)}`}
                    >
                      {ticket.status}
                    </span>
                    {/*
                    <span className="text-xs text-gray-500">
                      #{ticket.id} 
                      {new Date(ticket.updated_at).toLocaleDateString()}
                    </span>
                    */}
                    {/* <span className="text-xs text-gray-500">
                      #{ticket.id}
                      {new Date(ticket.updated_at).toLocaleString("en-US", {
                        month: "short",
                        day: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </span> */}
                    <span className="text-xs text-gray-500">
                      {new Date(ticket.updated_at).toLocaleString("en-US", {
                        month: "short",
                        day: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>

          <PaginationUtils
            pagination={pagination}
            onPageChange={(page) => fetchTickets(page, pagination.limit)}
            onLimitChange={(limit) => fetchTickets(1, limit)}
          />
        </div>

        <div className="xl:col-span-3 bg-white rounded shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Actions</h2>
          </div>

          {!selectedTicket ? (
            <div className="p-8 text-center text-gray-500">
              <FiTool className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              Select a ticket to perform actions.
            </div>
          ) : (
            <div className="p-4 space-y-5">
              <div className="bg-gray-50 border border-gray-200 rounded p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{selectedTicket.subject}</h3>
                    <p className="text-sm text-gray-600 mt-1">{selectedTicket.description}</p>
                  </div>
                  <div className="text-right">
                    <span
                      className={`inline-block text-xs font-medium px-2 py-1 rounded ${statusBadge(
                        selectedTicket.status
                      )}`}
                    >
                      {selectedTicket.status}
                    </span>
                  </div>
                </div>
              </div>

              {/*
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded p-4 space-y-3">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <FiEdit2 className="w-4 h-4" />
                    Update Details
                  </h4>
                  <input
                    type="text"
                    value={editForm.subject}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    placeholder="Subject"
                  />
                  <textarea
                    value={editForm.description}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, description: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    rows={3}
                    placeholder="Description"
                  />
                  <select
                    value={editForm.priority}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                  <button
                    onClick={handleUpdateDetails}
                    disabled={actionLoading || !canUpdateDetails}
                    className="px-3 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50"
                  >
                    {canUpdateDetails ? "Save Details" : "No Update Permission"}
                  </button>
                </div>

                <div className="border border-gray-200 rounded p-4 space-y-3">
                  <h4 className="font-medium text-gray-900">Address Ticket</h4>
                  <select
                    value={addressForm.status}
                    onChange={(e) =>
                      setAddressForm((prev) => ({ ...prev, status: e.target.value as TicketStatus }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                  <textarea
                    value={addressForm.resolution_note}
                    onChange={(e) =>
                      setAddressForm((prev) => ({ ...prev, resolution_note: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    rows={3}
                    placeholder="Resolution / handling note"
                  />
                  <button
                    onClick={handleAddressTicket}
                    disabled={actionLoading || !canAddressTicket}
                    className="px-3 py-2 text-sm bg-indigo-700 text-white rounded hover:bg-indigo-800 disabled:opacity-50"
                  >
                    {canAddressTicket ? "Submit Addressing" : "No Address Permission"}
                  </button>
                </div>
              </div>
              */}

              <div className="border border-gray-200 rounded p-4 space-y-3">
                <h4 className="font-medium text-gray-900">Update Status</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <select
                    value={statusForm.status}
                    onChange={(e) =>
                      setStatusForm((prev) => ({ ...prev, status: e.target.value as TicketStatus }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                    <option value="reopened">Reopened</option>
                  </select>
                  <input
                    type="text"
                    value={statusForm.resolution_note}
                    onChange={(e) =>
                      setStatusForm((prev) => ({ ...prev, resolution_note: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    placeholder="Resolution note (optional)"
                  />
                </div>
                <button
                  onClick={handleUpdateStatus}
                  disabled={actionLoading || !canUpdateTicketStatus}
                  className="px-3 py-2 text-sm bg-green-700 text-white rounded hover:bg-green-800 disabled:opacity-50"
                >
                  {canUpdateTicketStatus ? "Update Status" : "No Status Permission"}
                </button>
              </div>

              <div className="border border-gray-200 rounded">
                {/*
                <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 text-sm font-medium text-gray-700 flex items-center gap-2">
                  <FiMessageSquare className="w-4 h-4" />
                  Conversation
                </div>
                */}
                <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 text-sm font-medium text-gray-700 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FiMessageSquare className="w-4 h-4" />
                    Conversation
                  </div>
                  <button
                    type="button"
                    onClick={() => selectedTicketId && fetchTicketDetail(selectedTicketId)}
                    className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                    title="Refresh conversation"
                  >
                    <FiRefreshCw className="w-4 h-4" />
                  </button>
                </div>
                <div className="max-h-[280px] overflow-y-auto p-4 space-y-3">
                  {detailLoading ? (
                    <p className="text-sm text-gray-500">Loading messages...</p>
                  ) : orderedMessages.length === 0 ? (
                    <p className="text-sm text-gray-500">No messages yet.</p>
                  ) : (
                    orderedMessages.map((message) => (
                      <div key={message.id} className="bg-gray-50 border border-gray-200 rounded p-3">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{message.message}</p>
                        {/*
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(message.created_at).toLocaleString()}
                        </p>
                        */}
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(message.created_at).toLocaleString("en-US", {
                            month: "short",
                            day: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Add Message</label>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Reply on this ticket..."
                />
                <button
                  onClick={handleAddMessage}
                  disabled={actionLoading || !canAddMessage}
                  className="mt-2 px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50 flex items-center gap-2"
                >
                  <FiSend className="w-4 h-4" />
                  {canAddMessage ? "Send Message" : "No Message Permission"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketActionsPage;
